import { GenericError, LoadingSpinner } from '@psyomics/ui-components';
import { useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { ContentType, ReferralInvite } from '../../../features/referral/generated/referral';
import { useReferralInviteQuery } from '../../../features/referral/hooks/use-referral-invite-query';
import { useLandingContext } from '../../../shared/contexts/landing-context';

import { Welcome } from './views/welcome';

const WelcomePage = ({ invite }: { readonly invite?: ReferralInvite }) => {
  if (!invite) {
    return (
      <GenericError
        backgroundVariant="indigoPurple"
        illustration="NotFound"
        titleKey="page.viewReferral.errors.notFound"
      />
    );
  }

  return <Welcome invite={invite} />;
};

const getPresignUpText = (invite: ReferralInvite | undefined): { text: string; title: string } | undefined => {
  const content = invite?.referralSession.referral.organisation?.organisationContent.find(
    (c) => c.contentType === ContentType.PresignupText
  )?.content?.definition;
  const presignUpContent = content ? JSON.parse(content) : undefined;
  if (presignUpContent && 'title' in presignUpContent && 'text' in presignUpContent) {
    return presignUpContent;
  } else {
    return undefined;
  }
};

const Referral = () => {
  const { invitePublicId } = useParams<{ invitePublicId: string }>();
  const { setPreSignUpText } = useLandingContext();
  const [invite, fetching] = useReferralInviteQuery(invitePublicId ?? '');

  const preSignUpText = useMemo(() => getPresignUpText(invite), [invite]);

  useEffect(() => {
    setPreSignUpText(preSignUpText);
  }, [preSignUpText, setPreSignUpText]);

  if (!invitePublicId) return <Navigate to="/" />;
  if (fetching) return <LoadingSpinner />;

  return (
    <Routes>
      <Route element={<WelcomePage invite={invite} />} path="welcome" />
      <Route element={<GenericError titleKey="common.errors.notFound" />} path="/*"></Route>
    </Routes>
  );
};

export { Referral };
